import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { CarbonTable } from 'src/app/components/carbon-table/carbon-table.component';
import { CharacteristicConsultService } from 'src/app/pages/register/characteristic-consult/services/characteristic-consult.service';
import { CarbonModalService } from 'src/app/services/modal/carbon-modal.service';
import { OperationTransferenceService } from 'src/app/services/operation/operation-transference.service';
import { CarbonOperation } from 'src/app/shared/interfaces/carbon-operation';
import { OperationType } from 'src/app/shared/interfaces/operation-type.enum';
import { Pagination } from 'src/app/shared/interfaces/pagination';
import { AppModalService } from 'src/app/shared/services/app-modal.service';

import { OperationFilter } from '../../shared/operation-filter';
import { ModalBeneficiariesTableComponent } from '../modal-beneficiaries-table/modal-beneficiaries-table.component';
import { OperationListService } from '../shared/operation-list.service';
import { isB3WithdrawalTransferOperation } from '../shared/operation-b3-withdrawal-transfer-details.utils';
import { isRetirementOperation } from '../shared/operation-retirement-details.utils';
import { isAcxCustodyWithdrawalOperation } from '../shared/operation-custody-withdrawal-details.utils';
import { isAcxTradeOperation } from '../shared/operation-acx-trade-details.utils';
import { isB3TransferOperation } from '../shared/operation-transfer-details.utils';
import {
  OperationTypeBadge,
  resolveOperationTypeBadge,
} from '../shared/operation-type-badge.utils';

@Component({
  standalone: false,
  selector: 'app-operation-list-table',
  templateUrl: './operation-list-table.component.html',
  styleUrls: ['./operation-list-table.component.scss']
})
export class OperationListTableComponent extends CarbonTable<CarbonOperation> implements OnInit, OnDestroy {
  private static readonly PAGE_SIZE = 10;

  @Output() noItens = new EventEmitter<boolean>();
  @Output() valueTotal = new EventEmitter<number>();
  @Output() valueFilterTotal = new EventEmitter<number>();
  @Output() depositDetailRequested = new EventEmitter<CarbonOperation>();
  @Output() retirementDetailRequested = new EventEmitter<CarbonOperation>();
  @Output() transferDetailRequested = new EventEmitter<CarbonOperation>();
  @Output() custodyWithdrawalDetailRequested = new EventEmitter<CarbonOperation>();
  @Output() acxTradeDetailRequested = new EventEmitter<CarbonOperation>();
  @Output() b3WithdrawalTransferDetailRequested = new EventEmitter<CarbonOperation>();

  private readonly destroy$ = new Subject<void>();

  override pagination: Pagination<CarbonOperation> = OperationListTableComponent.createPagination();

  constructor(
    private readonly operationListService: OperationListService,
    private readonly operationConfirmationService: OperationTransferenceService,
    private readonly carbonModalService: CarbonModalService,
    private readonly characteristicConsultService: CharacteristicConsultService,
    private readonly modalService: AppModalService,
  ) {
    super();
  }

  override resetPagination(): void {
    this.pagination = OperationListTableComponent.createPagination();
  }

  ngOnInit(): void {
    this.filterInit();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get operationsFoundLabel(): string {
    const count = this.totalItens ?? 0;
    return count === 1
      ? '1 resultado encontrado para o filtro selecionado'
      : `${count} resultados encontrados para o filtro selecionado`;
  }

  get recordsPerPageLabel(): string {
    const pageSize = this.pagination.size ?? OperationListTableComponent.PAGE_SIZE;
    return pageSize === 1
      ? 'Mostrando 1 registro por página'
      : `Mostrando ${pageSize} registros por página`;
  }

  trackByOperationCode(_index: number, operation: CarbonOperation): string {
    return `${operation.operationCode ?? 'operation'}-${_index}`;
  }

  formatQuantity(value?: number | null): string {
    if (value === null || value === undefined) {
      return '-';
    }

    return `${value}`;
  }

  getOperationDetailLabel(operation: CarbonOperation): string {
    return `Visualizar detalhes da operação ${operation.operationCode || ''}`.trim();
  }

  getOperationTypeBadge(operation: CarbonOperation): OperationTypeBadge {
    return resolveOperationTypeBadge(operation);
  }

  getOperationControlNumber(operation: CarbonOperation): string {
    const value = operation.operationCode?.trim();
    return value && value !== '-' ? value : '';
  }

  getOperationControlNumberAriaLabel(operation: CarbonOperation): string {
    const value = this.getOperationControlNumber(operation);
    return value
      ? `Número de controle ${value}. Clique para copiar.`
      : 'Número de controle indisponível';
  }

  copyOperationControlNumber(operation: CarbonOperation, event: Event): void {
    event.stopPropagation();

    const value = this.getOperationControlNumber(operation);
    if (!value) {
      return;
    }

    void navigator.clipboard.writeText(value);
  }

  onPaginationChange(event: Event): void {
    const customEvent = event as CustomEvent<{ current: number; previous: number }>;
    const nextPage = customEvent.detail?.current;

    if (nextPage && nextPage !== this.pagination.page) {
      this.onPageChange(nextPage);
    }
  }

  onPageChange(currentPage: number): void {
    this.pagination.page = currentPage;
    const filters = this.operationListService.filters.getValue();
    this.emitFilterTotal(filters);
    this.getCarbonOperations(filters);
  }

  openDetail(carbon: CarbonOperation): void {
    const rawType = carbon.rawTypeCode ?? '';

    if (rawType === OperationType.DEPOSIT) {
      this.depositDetailRequested.emit(carbon);
      return;
    }

    if (isRetirementOperation(carbon)) {
      this.retirementDetailRequested.emit(carbon);
      return;
    }

    if (isB3TransferOperation(carbon)) {
      this.transferDetailRequested.emit(carbon);
      return;
    }

    if (isAcxCustodyWithdrawalOperation(carbon)) {
      this.custodyWithdrawalDetailRequested.emit(carbon);
      return;
    }

    if (isAcxTradeOperation(carbon)) {
      this.acxTradeDetailRequested.emit(carbon);
      return;
    }

    if (isB3WithdrawalTransferOperation(carbon)) {
      this.b3WithdrawalTransferDetailRequested.emit(carbon);
      return;
    }

    if (this.isWithdrawal(carbon)) {
      this.characteristicConsultService.getBeneficiaries(carbon.operationId).subscribe({
        next: (response: any) => {
          const beneficiaries = response.data || [];
          this.modalService.open(ModalBeneficiariesTableComponent, {
            data: beneficiaries,
            comitente: carbon.holderCode,
            hasBackdrop: true,
            closeOnEsc: true,
            closeButton: false,
            size: 'large',
            theme: 'light',
          });
        },
        error: () => {
          this.modalService.open(ModalBeneficiariesTableComponent, {
            data: [],
            comitente: carbon.holderCode,
            hasBackdrop: true,
            closeOnEsc: true,
            closeButton: false,
            size: 'large',
            theme: 'light',
          });
        },
      });
      return;
    }

    this.characteristicConsultService.getCarbonCreditById(carbon.carbonCreditCode).subscribe(
      (response: any) => {
        const carbonCredit = response?.data ?? response;
        this.carbonModalService.openCarbonCreditDetailModal(carbonCredit);
      },
    );
  }

  private getCarbonOperations(filters: OperationFilter): void {
    this.operationConfirmationService.getOperationPendingApproval(filters, false, this.pagination).subscribe(response => {
      this.valueTotal.emit(response.total);
      this.itens = response.results;
      this.totalItens = response.total || 0;
      this.handleTotalPages(null);
      this.noItens.emit(this.totalItens === 0);
    });
  }

  private filterInit(): void {
    this.operationListService.filters
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        if (this.shouldLoadOperations(res)) {
          this.resetPagination();
          this.getCarbonOperations(res);
          this.emitFilterTotal(res);
        }
      });
  }

  private shouldLoadOperations(filters: OperationFilter): boolean {
    if (this.operationListService.personalizedFilterActive) {
      return true;
    }

    return !!(filters.initialDate && filters.finalDate);
  }

  private emitFilterTotal(filters: OperationFilter): void {
    setTimeout(() => {
      this.valueFilterTotal.emit(this.countActiveFilters(filters));
    });
  }

  private countActiveFilters(filters: OperationFilter): number {
    let count = 0;

    if (!this.isEmptyValue(filters.operationCode)) count++;
    if (!this.isEmptyValue(filters.initialDate)) count++;
    if (!this.isEmptyValue(filters.finalDate)) count++;
    if (!this.isEmptyValue(filters.holderCode)) count++;
    if (!this.isEmptyValue(filters.purchaserCode)) count++;
    if (!this.isEmptyValue(filters.carbonCreditCode)) count++;

    if (filters.typeCode !== undefined && filters.typeCode !== null) count++;

    return count;
  }

  private isEmptyValue(value: unknown): boolean {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string') return value.trim().length === 0;
    if (value instanceof Date) return isNaN(value.getTime());
    return false;
  }

  private isWithdrawal(operation: CarbonOperation): boolean {
    const rawType = operation.rawTypeCode ?? operation.typeCode;
    return rawType === OperationType.WITHDRAW || operation.typeCode === 'Retirada';
  }

  private static createPagination(): Pagination<CarbonOperation> {
    return {
      page: 1,
      size: OperationListTableComponent.PAGE_SIZE,
      results: [{}] as CarbonOperation[],
    };
  }
}
